import React, { useState, useEffect } from "react";
import LabeledTextInput from "../../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import { LabelModes } from "../../../ui-components/molecules/labeled-inputs/labeledInput";
import { ReactComponent as AddressIcon } from "images/address.svg";
import { useDispatch } from "react-redux";
import { addressSlice } from "slices/address-manager/addressSlice";
import Loading from "ui-components/loading/loading";
import { useSelector } from "react-redux";
import { addressSelector } from "slices/address-manager/addressSlice";
import { AddressState } from "slices/address-manager/AddressState";
import InfiniteScrollableList from "../common/infiniteScrollableList";
import searchIcon from "images/search_gray.svg";
import './sideAddressList.scss'
import { useAddressList } from "../hooks/useAddressList";

const SideAddreessList = ({ terminal, handleAddressSelect, selectedFilter }: {
    terminal: string, handleAddressSelect:
    (address: string) => void, selectedFilter: string
}): React.ReactElement => {
    const [input, setInput] = useState<string>("");
    const dispatch = useDispatch();
    const [selectedAddressIndex, setSelectedAddressIndex] = useState<string | null>(null);
    const addressManagerState: AddressState = useSelector(addressSelector);
    const searchString = addressManagerState.searchString;

    const { data, fetchNextPage, hasNextPage, isLoading, isRefetching } = useAddressList(terminal, searchString, "", "", "", selectedFilter);

    const items = data?.pages.flatMap((page) => page.content) ?? [];
    const itemCount = hasNextPage ? items.length + 1 : items.length;

    const isItemLoaded = (index: number) => index < items.length;

    const loadMoreItems = async (startIndex: number, stopIndex: number) => {
        if (hasNextPage) {
            fetchNextPage();
        }
    };

    useEffect(() => {
        const handler =
            setTimeout(() =>
                dispatch(addressSlice.actions.setSearchString({ searchString: input })), 1000);
        return () => clearTimeout(handler);
    }, [input, dispatch]);

    useEffect(() => {
        // reset
        if(searchString === ""){
            setInput("");
        }
    }, [searchString]);


    useEffect(() => {
        if (data) {
            dispatch(addressSlice.actions.setAddressList({ data: data.pages.flatMap((page) => page.content), count: data.pages.flatMap((page) => page.content).length }));
            //if selectedAddressId is null then set the first address as selectedAddress
            if (addressManagerState.selectedAddressId === null) {
                dispatch(addressSlice.actions.setAddressById({ id: data.pages.flatMap((page) => page.content)?.[0]?.id, address: data.pages.flatMap((page) => page.content)?.[0] }));
                setSelectedAddressIndex(data.pages.flatMap((page) => page.content)?.[0]?.formattedAddressId ?? "");
            }
            if (addressManagerState.selectedAddressId) {
                setSelectedAddressIndex(addressManagerState.selectedAddress?.formattedAddressId ?? "");
            }
        }
        //eslint-disable-next-line
    }, [data, addressManagerState.selectedAddressId]);

    const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
        if (!isItemLoaded(index)) {
            return (
                <div style={{ ...style, padding: 10 }}>
                    Loading...
                </div>
            );
        }

        return (
            <div onClick={() => { setSelectedAddressIndex(items?.[index]?.formattedAddressId ?? ""); handleAddressSelect(items?.[index]?.id) }} style={{ ...style, padding: 5 }}>
                <ul>
                    <li key={index} style={items?.[index]?.formattedAddressId === selectedAddressIndex ? { backgroundColor: "#2F80ED14", border: "1px solid #2F80ED" } : {}}>
                        <div className="__boxinfo">
                            <div className="__address-container">
                                <div className="__addressLine__container">
                                    <div className="__addressLine">
                                        <span><AddressIcon className="__addrIcon" /></span>
                                        <span className="__addrName">{items[index].line1}</span>
                                    </div>
                                </div>
                                <div className="_addrCity">{items[index].city.toLowerCase().capitalize() + ", " + items[index].state + " " + items[index].zip}</div>
                                <div className="_addrId">
                                    Address ID: {items[index].formattedAddressId}
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        );
    };

    return (
        <div className="xgs-sideaddress-list">
            <div className="notes-widget__recent__filter">
                <img src={searchIcon} alt="search" className="notes-widget__recent__filter__searchIcon" />
                <LabeledTextInput
                    name="searchString"
                    labelMode={LabelModes.column}
                    className="xgs-sideaddress-list__input"
                    type="text"
                    value={input}
                    onKeyDown={(e) => (e.key === "Enter")}
                    onChange={(e) => setInput(e.currentTarget.value)}
                    placeholder="Search addresses..."
                    clearable
                    onClear={() => setInput("")}
                />
            </div>
            <div className="scrollable_list">
                <div style={{ flex: 1 }}>
                    {(isLoading || isRefetching) ? (
                        <div className="xgs-sideaddress-list__loading"><Loading isLoading={true} /></div>
                    ) : items.length === 0 ? (
                        <div className="xgs-sideaddress-list__no-results">
                            No addresses found{searchString ? ` for "${searchString}"` : ''}
                        </div>
                    ) : (
                        <InfiniteScrollableList
                            isItemLoaded={isItemLoaded}
                            itemCount={itemCount}
                            loadMoreItems={loadMoreItems}
                            children={Row}
                            itemSize={80}
                        />
                    )}
                </div>
            </div>
        </div>
    )
}

export default SideAddreessList;